import React, { useEffect, useRef } from "react";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import layout from "simple-keyboard-layouts/build/layouts/arabic";
import CustomDialog from "../custom-dialog/custom-dialog";
import { Button, Box, Typography } from "@mui/material";
import { useSnackBar } from "../../../contexts/snackbar";

interface ArabicKeyboardDialogProps {
  value: string;
  setValue: (value: string) => void;
  onClose: () => void;
}

const ArabicKeyboardDialog: React.FC<ArabicKeyboardDialogProps> = ({ value, setValue, onClose }) => {
  const keyboardRef = useRef<any>(null);
  const {showMessage} = useSnackBar();

  // Keep virtual keyboard synced with external value (e.g., if user edits text elsewhere)
  useEffect(() => {
    if (keyboardRef.current) {
      keyboardRef.current.setInput(value);
    }
  }, [value]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      showMessage("Copied to clipboard", "success")
    } catch {
      alert("Failed to copy text");
    }
  };

  const handleClear = () => {
    setValue("");
    if (keyboardRef.current) keyboardRef.current.clearInput();
  };

  return (
    <CustomDialog open={true} maxWidth={"md"} onClose={onClose}>
      <Box display="flex" flexDirection="column" gap={2} p={2}>
        {/* Text Preview */}
        <Typography
          variant="body1"
          sx={{
            p: 1.5,
            border: "1px solid #ccc",
            borderRadius: 2,
            minHeight: 50,
            textAlign: "right",
            direction: "rtl",
            fontSize: "1.2rem",
            background: "#fafafa",
          }}
        >
          {value}
        </Typography>

        {/* Action Buttons */}
        <Box display="flex" justifyContent="space-between" gap={2}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleClear}
            fullWidth
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCopy}
            fullWidth
          >
            Copy
          </Button>
        </Box>

        {/* Arabic Keyboard */}
        <Keyboard
          keyboardRef={(r) => (keyboardRef.current = r)}
          layout={layout.layout}
          layoutName="default"
          onChange={setValue}
          input={value}
        />
      </Box>
    </CustomDialog>
  );
};

export default ArabicKeyboardDialog;
